(function () {
  'use strict';

  if (window.WagmadeHomepageLoaded) return;

  function createStaticController(root, activeKey, modeKey, modeValue) {
    var initialized = false;

    function reset() {
      delete root.dataset[activeKey];
      delete root.dataset[modeKey];
    }

    return {
      init: function () {
        if (initialized) this.destroy();
        root.dataset[activeKey] = 'static';
        root.dataset[modeKey] = modeValue;
        initialized = true;
      },
      refresh: function () {
        this.destroy();
        this.init();
      },
      destroy: function () {
        reset();
        initialized = false;
      },
    };
  }

  function createHero(root) {
    var overlayFrame = null;
    var depthFrame = null;
    var mediaQuery = null;
    var desktopQuery = null;
    var depthPointerQuery = null;
    var depthCleanup = null;
    var initialized = false;
    var ownerDocument = root.ownerDocument;
    var view = ownerDocument.defaultView || window;

    function reducedMotion() {
      return mediaQuery && mediaQuery.matches;
    }

    function cancelOverlayUpdate() {
      if (overlayFrame === null) return;
      if (view.cancelAnimationFrame) view.cancelAnimationFrame(overlayFrame);
      else view.clearTimeout(overlayFrame);
      overlayFrame = null;
    }

    function cancelDepthUpdate() {
      if (depthFrame === null) return;
      if (view.cancelAnimationFrame) view.cancelAnimationFrame(depthFrame);
      else view.clearTimeout(depthFrame);
      depthFrame = null;
    }

    function addMediaChangeListener(query, handler) {
      if (!query) return;
      if (query.addEventListener) query.addEventListener('change', handler);
      else if (query.addListener) query.addListener(handler);
    }

    function removeMediaChangeListener(query, handler) {
      if (!query) return;
      if (query.removeEventListener) query.removeEventListener('change', handler);
      else if (query.removeListener) query.removeListener(handler);
    }

    function clearDepthPointer() {
      if (depthCleanup) depthCleanup();
      depthCleanup = null;
      cancelDepthUpdate();
      delete root.dataset.wmDepthMotion;
    }

    function syncDepthPointer() {
      clearDepthPointer();
      if (root.dataset.wmHeroArtDirection === 'photographic') return;

      var stages = root.querySelectorAll('[data-wm-depth-stage]');
      if (!stages.length || reducedMotion()) {
        root.dataset.wmDepthMotion = 'static';
        return;
      }

      var canTrackPointer = !!(depthPointerQuery && depthPointerQuery.matches);
      // TASK-P1-110: mirror the supplied DepthText defaults. The neutral base
      // pose is a small perspective angle; the pointer only adds to it. When
      // fine-pointer input is unavailable, the reference's subtle auto-orbit
      // keeps the extrusion legible without requiring a hover target.
      var baseRotation = { x: -2.4, y: 3.15 };
      var tilt = 7.5;
      // Keep the supplied orbit amplitude stable for touch/coarse fallbacks.
      var verticalTilt = 9;
      // TASK-P1-114: give fine-pointer vertical movement more presence while
      // leaving the horizontal axis and touch orbit on their existing values.
      var pointerVerticalTilt = 18;
      var smoothing = .14;
      var orbitSpeed = .35;
      // TASK-P1-115: normalize against the visible sticky frame, not the
      // taller scroll container that owns the Hero narrative.
      var pointerSurface = root.querySelector('.wm-hero__frame') || root;
      var current = { x: baseRotation.x, y: baseRotation.y };
      var target = { x: baseRotation.x, y: baseRotation.y };
      var activePointer = false;
      var startTime = nowMillis();

      function applyDepthTransform() {
        var transform = 'rotateX(' + current.x.toFixed(3) + 'deg) rotateY(' + current.y.toFixed(3) + 'deg)';
        stages.forEach(function (stage) {
          stage.style.transform = transform;
        });
      }

      function tickDepth() {
        depthFrame = null;
        if (!canTrackPointer || !activePointer) {
          var elapsed = (nowMillis() - startTime) / 1000;
          var orbit = elapsed * orbitSpeed * Math.PI * 2;
          var fallbackAmount = canTrackPointer ? .18 : .55;
          target.x = baseRotation.x + Math.sin(orbit) * verticalTilt * fallbackAmount;
          target.y = baseRotation.y + Math.cos(orbit * .85) * tilt * fallbackAmount;
        }
        current.x += (target.x - current.x) * smoothing;
        current.y += (target.y - current.y) * smoothing;
        applyDepthTransform();
        scheduleDepthUpdate();
      }

      function scheduleDepthUpdate() {
        if (depthFrame !== null) return;
        if (view.requestAnimationFrame) depthFrame = view.requestAnimationFrame(tickDepth);
        else depthFrame = view.setTimeout(tickDepth, 16);
      }

      function onDepthPointerMove(event) {
        var rect = pointerSurface.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        activePointer = true;
        var x = clamp((event.clientX - (rect.left + rect.width / 2)) / (rect.width * .8), -1, 1);
        var y = clamp((event.clientY - (rect.top + rect.height / 2)) / (rect.height * .8), -1, 1);
        target.x = baseRotation.x - y * pointerVerticalTilt;
        target.y = baseRotation.y + x * tilt;
        scheduleDepthUpdate();
      }

      function resetDepthPointer(event) {
        // The fixed Header sits above the sticky frame at the top of the
        // viewport. Crossing into it is still a continuous Hero gesture, not
        // a real exit, so keep the last pointer target until the frame is
        // re-entered or the window actually loses focus.
        if (event && event.relatedTarget && event.relatedTarget.closest && event.relatedTarget.closest('.wagmade-header')) {
          return;
        }
        activePointer = false;
        scheduleDepthUpdate();
      }

      if (canTrackPointer) {
        root.addEventListener('pointermove', onDepthPointerMove, { passive: true });
        root.addEventListener('pointerleave', resetDepthPointer);
        view.addEventListener('blur', resetDepthPointer);
      }

      applyDepthTransform();
      root.dataset.wmDepthMotion = 'orbit';
      if (canTrackPointer) root.dataset.wmDepthMotion = 'pointer';
      scheduleDepthUpdate();

      depthCleanup = function () {
        if (canTrackPointer) {
          root.removeEventListener('pointermove', onDepthPointerMove);
          root.removeEventListener('pointerleave', resetDepthPointer);
          view.removeEventListener('blur', resetDepthPointer);
        }
        stages.forEach(function (stage) {
          stage.style.removeProperty('transform');
        });
      };
    }

    function updateOverlay() {
      overlayFrame = null;
      if (root.dataset.wmMediaEmpty === 'true' || root.dataset.wmHeroCover !== 'true' || !desktopQuery || !desktopQuery.matches || reducedMotion()) {
        root.style.removeProperty('--wm-home-overlay-progress');
        root.style.removeProperty('--wm-home-overlay-fade');
        root.style.removeProperty('--wm-home-overlay-shift');
        delete root.dataset.wmHomeOverlay;
        delete root.dataset.wmHeroTransition;
        return;
      }
      var pageY = view.scrollY || ownerDocument.documentElement.scrollTop || 0;
      var rootTop = root.getBoundingClientRect().top + pageY;
      // Match the full sticky travel, including the incoming section cover.
      var distance = Math.max(root.offsetHeight - (view.innerHeight || 1), 1);
      var progress = clamp((pageY - rootTop) / distance, 0, 1);
      var fadeProgress = clamp((progress - .04) / .52, 0, 1);
      root.style.setProperty('--wm-home-overlay-progress', progress.toFixed(4));
      root.style.setProperty('--wm-home-overlay-fade', fadeProgress.toFixed(4));
      root.style.setProperty('--wm-home-overlay-shift', String((fadeProgress * -1.25).toFixed(4)) + 'rem');
      root.dataset.wmHeroTransition = 'cover-v2';
    }

    function scheduleOverlayUpdate() {
      if (overlayFrame !== null) return;
      if (view.requestAnimationFrame) overlayFrame = view.requestAnimationFrame(updateOverlay);
      else overlayFrame = view.setTimeout(updateOverlay, 16);
    }

    function onOverlayScroll() {
      scheduleOverlayUpdate();
    }

    function onOverlayResize() {
      scheduleOverlayUpdate();
      if (initialized) syncDepthPointer();
    }

    function onMotionChange() {
      scheduleOverlayUpdate();
      if (initialized) syncDepthPointer();
    }

    function onDepthCapabilityChange() {
      if (initialized) syncDepthPointer();
    }

    return {
      init: function () {
        if (initialized) this.destroy();
        mediaQuery = view.matchMedia('(prefers-reduced-motion: reduce)');
        desktopQuery = view.matchMedia('(min-width: 1024px) and (min-height: 650px)');
        depthPointerQuery = view.matchMedia('(hover: hover) and (pointer: fine)');
        addMediaChangeListener(mediaQuery, onMotionChange);
        addMediaChangeListener(desktopQuery, onOverlayResize);
        addMediaChangeListener(depthPointerQuery, onDepthCapabilityChange);
        ownerDocument.addEventListener('scroll', onOverlayScroll, { passive: true });
        view.addEventListener('resize', onOverlayResize);
        root.dataset.wmHomeMode = 'static';
        updateOverlay();
        initialized = true;
        syncDepthPointer();
      },
      refresh: function () {
        this.destroy();
        this.init();
      },
      destroy: function () {
        clearDepthPointer();
        cancelOverlayUpdate();
        ownerDocument.removeEventListener('scroll', onOverlayScroll);
        view.removeEventListener('resize', onOverlayResize);
        removeMediaChangeListener(mediaQuery, onMotionChange);
        removeMediaChangeListener(desktopQuery, onOverlayResize);
        removeMediaChangeListener(depthPointerQuery, onDepthCapabilityChange);
        root.style.removeProperty('--wm-home-overlay-progress');
        root.style.removeProperty('--wm-home-overlay-fade');
        root.style.removeProperty('--wm-home-overlay-shift');
        delete root.dataset.wmHomeMode;
        delete root.dataset.wmHomeOverlay;
        delete root.dataset.wmHeroTransition;
        depthPointerQuery = null;
        initialized = false;
      },
    };
  }

  function createCollection(root) {
    return createStaticController(root, 'wmCollectionActive', 'wmCollectionMode', 'product-led');
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function nowMillis() {
    if (window.performance && window.performance.now) return window.performance.now();
    return Date.now();
  }

  function createScrollStory(root, itemSelector, options) {
    var initialized = false;
    var enhanced = false;
    var modeSet = false;
    var activeIndex = -1;
    var frame = null;
    var mediaQuery = null;
    var desktopQuery = null;
    var items = [];
    var view = root.ownerDocument.defaultView || window;
    var ownerDocument = root.ownerDocument;

    function reducedMotion() {
      return mediaQuery && mediaQuery.matches;
    }

    function shouldEnhance() {
      return items.length > 1 && !reducedMotion() && desktopQuery && desktopQuery.matches;
    }

    function rememberAria(item) {
      if (item.dataset.wmStoryAriaHidden === undefined) {
        item.dataset.wmStoryAriaHidden = item.getAttribute('aria-hidden') || '';
      }
    }

    function restoreAria(item) {
      if (item.dataset.wmStoryAriaHidden === '') item.removeAttribute('aria-hidden');
      else if (item.dataset.wmStoryAriaHidden !== undefined) item.setAttribute('aria-hidden', item.dataset.wmStoryAriaHidden);
      delete item.dataset.wmStoryAriaHidden;
    }

    function setItemTabState(item, isActive, interactive) {
      rememberAria(item);
      item.setAttribute('aria-hidden', interactive && !isActive ? 'true' : 'false');
      var controls = [];
      if (/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(item.tagName)) controls.push(item);
      item.querySelectorAll('a, button, input, select, textarea').forEach(function (control) {
        controls.push(control);
      });
      controls.forEach(function (control) {
        if (interactive && !isActive) {
          if (control.dataset.wmStoryTabindex === undefined) control.dataset.wmStoryTabindex = control.getAttribute('tabindex') || '';
          control.setAttribute('tabindex', '-1');
        } else if (control.dataset.wmStoryTabindex !== undefined) {
          if (control.dataset.wmStoryTabindex === '') control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', control.dataset.wmStoryTabindex);
          delete control.dataset.wmStoryTabindex;
        }
      });
    }

    function resetItems() {
      items.forEach(function (item) {
        var visual = options.visualSelector ? item.querySelector(options.visualSelector) : item;
        item.classList.remove('is-active');
        item.style.removeProperty('opacity');
        item.style.removeProperty('transform');
        item.style.removeProperty('z-index');
        if (visual && visual !== item) {
          visual.style.removeProperty('transform');
          visual.style.removeProperty('will-change');
        }
        if (options.contentSelector) {
          var content = item.querySelector(options.contentSelector);
          if (content) content.style.removeProperty('opacity');
        }
        setItemTabState(item, true, false);
        restoreAria(item);
      });
      if (options.collage) {
        root.style.removeProperty('--wm-movement-columns');
        root.style.removeProperty('--wm-movement-rows');
      }
      activeIndex = -1;
    }

    function applyItemState(nextIndex) {
      if (activeIndex === nextIndex) return;
      activeIndex = nextIndex;
      root.dataset[options.activeKey] = String(nextIndex);
      items.forEach(function (item, index) {
        var isActive = index === nextIndex;
        item.classList.toggle('is-active', isActive);
        setItemTabState(item, isActive, !options.collage);
      });
    }

    function update() {
      frame = null;
      if (!enhanced) return;
      var pageY = view.scrollY || ownerDocument.documentElement.scrollTop || 0;
      var rootTop = root.getBoundingClientRect().top + pageY;
      var scrollDistance = Math.max(root.offsetHeight - view.innerHeight, 1);
      var progress = clamp((pageY - rootTop) / scrollDistance, 0, 1);
      if (options.coverDistance) {
        var coverDistance = Math.min(options.coverDistance(view), scrollDistance);
        progress = clamp((pageY - rootTop - coverDistance) / Math.max(scrollDistance - coverDistance, 1), 0, 1);
      }
      var scaledProgress = options.collage ? progress * Math.max(items.length - 1, 1) : progress * items.length;
      var nextIndex = options.collage
        ? Math.min(items.length - 1, Math.round(scaledProgress))
        : Math.min(items.length - 1, Math.floor(scaledProgress));
      root.style.setProperty('--wm-story-progress', progress.toFixed(4));
      applyItemState(nextIndex);

      if (options.collage) {
        var focusValues = items.map(function (item, itemIndex) {
          return Math.max(0, 1 - Math.abs(scaledProgress - itemIndex));
        });
        var leftFocus = focusValues[0] || 0;
        var rightFocus = focusValues.slice(1).reduce(function (maximum, value) {
          return Math.max(maximum, value);
        }, 0);
        var topFocus = focusValues[1] || 0;
        var bottomFocus = focusValues[2] || 0;
        root.style.setProperty('--wm-movement-columns', String(1 + leftFocus * 1.4) + 'fr ' + String(1 + rightFocus * 1.4) + 'fr');
        root.style.setProperty('--wm-movement-rows', String(1 + topFocus * 1.2) + 'fr ' + String(1 + bottomFocus * 1.2) + 'fr');
      }

      items.forEach(function (item, index) {
        if (options.collage) {
          item.style.opacity = '1';
          item.style.transform = 'none';
          item.style.zIndex = String(index + 1);
          var collageContent = options.contentSelector ? item.querySelector(options.contentSelector) : null;
          if (collageContent) collageContent.style.opacity = index === nextIndex ? '1' : '.72';
          return;
        }
        var phase = scaledProgress - index;
        var direction = index % 2 === 0 ? 1 : -1;
        var opacity = 0;
        var offset = 24;
        var scale = direction > 0 ? 1.06 : 0.96;

        if (phase >= 0 && phase <= 1) {
          opacity = 1;
          offset = (1 - phase) * 24;
          scale = direction > 0 ? 1.06 - phase * 0.06 : 0.96 + phase * 0.04;
        } else if (phase > 1) {
          var exit = clamp(phase - 1, 0, 1);
          opacity = Math.max(0, 1 - exit * 1.1);
          offset = -exit * 24;
          scale = direction > 0 ? 1 - exit * 0.02 : 1 + exit * 0.02;
        }

        var visual = options.visualSelector ? item.querySelector(options.visualSelector) : item;
        item.style.opacity = String(opacity);
        if (visual && visual !== item) {
          visual.style.transform = 'translate3d(0, ' + String(offset) + 'px, 0) scale(' + String(scale) + ')';
          visual.style.willChange = 'transform';
        } else {
          item.style.transform = 'translate3d(0, ' + String(offset) + 'px, 0) scale(' + String(scale) + ')';
        }
        if (options.contentSelector) {
          var content = item.querySelector(options.contentSelector);
          if (content) content.style.opacity = index === nextIndex ? '1' : '0';
        }
        item.style.zIndex = String(index === nextIndex ? items.length + 1 : index + 1);
      });
    }

    function schedule() {
      if (!enhanced || frame !== null) return;
      if (view.requestAnimationFrame) frame = view.requestAnimationFrame(update);
      else frame = view.setTimeout(update, 16);
    }

    function cancelScheduledUpdate() {
      if (frame === null) return;
      if (view.cancelAnimationFrame) view.cancelAnimationFrame(frame);
      else view.clearTimeout(frame);
      frame = null;
    }

    function onScroll() {
      schedule();
    }

    function onResize() {
      var nextEnhanced = shouldEnhance();
      if (nextEnhanced !== enhanced) setMode();
      else schedule();
    }

    function onMotionChange() {
      setMode();
    }

    function setMode() {
      items = Array.prototype.slice.call(root.querySelectorAll(itemSelector));
      var nextEnhanced = shouldEnhance();
      if (modeSet && enhanced === nextEnhanced) {
        if (enhanced) schedule();
        return;
      }

      cancelScheduledUpdate();
      ownerDocument.removeEventListener('scroll', onScroll);
      resetItems();
      enhanced = nextEnhanced;
      modeSet = true;

      if (enhanced) {
        root.dataset[options.activeKey] = '0';
        root.dataset[options.modeKey] = options.enhancedMode;
        root.dataset[options.enhancedKey] = 'true';
        root.style.setProperty('--wm-story-count', String(items.length));
        ownerDocument.addEventListener('scroll', onScroll, { passive: true });
        schedule();
      } else {
        root.dataset[options.activeKey] = 'static';
        root.dataset[options.modeKey] = options.staticMode;
        delete root.dataset[options.enhancedKey];
      root.style.removeProperty('--wm-story-count');
      root.style.removeProperty('--wm-story-progress');
      root.style.removeProperty('--wm-movement-columns');
      root.style.removeProperty('--wm-movement-rows');
      }
    }

    return {
      init: function () {
        if (initialized) this.destroy();
        mediaQuery = view.matchMedia('(prefers-reduced-motion: reduce)');
        desktopQuery = view.matchMedia('(min-width: 701px)');
        if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', onMotionChange);
        else if (mediaQuery.addListener) mediaQuery.addListener(onMotionChange);
        if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onResize);
        else if (desktopQuery.addListener) desktopQuery.addListener(onResize);
        view.addEventListener('resize', onResize);
        setMode();
        initialized = true;
      },
      refresh: function () {
        this.destroy();
        this.init();
      },
      destroy: function () {
        cancelScheduledUpdate();
        ownerDocument.removeEventListener('scroll', onScroll);
        view.removeEventListener('resize', onResize);
        if (mediaQuery) {
          if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', onMotionChange);
          else if (mediaQuery.removeListener) mediaQuery.removeListener(onMotionChange);
        }
        if (desktopQuery) {
          if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onResize);
          else if (desktopQuery.removeListener) desktopQuery.removeListener(onResize);
        }
        resetItems();
        delete root.dataset[options.activeKey];
        delete root.dataset[options.modeKey];
        delete root.dataset[options.enhancedKey];
        root.style.removeProperty('--wm-story-count');
        root.style.removeProperty('--wm-story-progress');
        enhanced = false;
        modeSet = false;
        mediaQuery = null;
        desktopQuery = null;
        initialized = false;
      },
    };
  }

  function createCategory(root) {
    var initialized = false;
    var enhanced = false;
    var modeSet = false;
    var mediaQuery = null;
    var desktopQuery = null;
    var view = root.ownerDocument.defaultView || window;

    function reducedMotion() {
      return mediaQuery && mediaQuery.matches;
    }

    function cards() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-wm-category-cards] > .wm-category-focus__card'));
    }

    function shouldEnhance() {
      return root.dataset.wmHasEmptyMedia !== 'true' && cards().length > 0 && root.dataset.wmCategoryCover === 'true' && !reducedMotion() && desktopQuery && desktopQuery.matches;
    }

    function setMode() {
      var nextEnhanced = shouldEnhance();
      if (modeSet && enhanced === nextEnhanced) {
        // Theme Editor section replacement and hot reload can clear dataset
        // attributes without changing the media query state. Re-assert the
        // visual contract so the Hero cover cannot silently fall back to flow.
        if (enhanced) {
          root.dataset.wmCategoryMode = 'cover-v2';
          root.dataset.wmCategoryTransition = 'cover-v2';
          root.style.setProperty('--wm-story-count', String(cards().length));
        } else {
          root.dataset.wmCategoryMode = 'four-up';
          delete root.dataset.wmCategoryTransition;
        }
        return;
      }
      enhanced = nextEnhanced;
      modeSet = true;
      root.style.removeProperty('--wm-story-count');
      if (enhanced) {
        root.dataset.wmCategoryMode = 'cover-v2';
        root.dataset.wmCategoryTransition = 'cover-v2';
        root.style.setProperty('--wm-story-count', String(cards().length));
      } else {
        root.dataset.wmCategoryMode = 'four-up';
        delete root.dataset.wmCategoryTransition;
      }
    }

    return {
      init: function () {
        if (initialized) this.destroy();
        mediaQuery = view.matchMedia('(prefers-reduced-motion: reduce)');
        desktopQuery = view.matchMedia('(min-width: 1024px)');
        if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', setMode);
        else if (mediaQuery.addListener) mediaQuery.addListener(setMode);
        if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', setMode);
        else if (desktopQuery.addListener) desktopQuery.addListener(setMode);
        view.addEventListener('resize', setMode);
        setMode();
        initialized = true;
      },
      refresh: function () {
        this.destroy();
        this.init();
      },
      destroy: function () {
        view.removeEventListener('resize', setMode);
        if (mediaQuery) {
          if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', setMode);
          else if (mediaQuery.removeListener) mediaQuery.removeListener(setMode);
        }
        if (desktopQuery) {
          if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', setMode);
          else if (desktopQuery.removeListener) desktopQuery.removeListener(setMode);
        }
        delete root.dataset.wmCategoryMode;
        delete root.dataset.wmCategoryTransition;
        root.style.removeProperty('--wm-story-count');
        enhanced = false;
        modeSet = false;
        mediaQuery = null;
        desktopQuery = null;
        initialized = false;
      },
    };
  }

  function createMovementTimeline(root, itemSelector) {
    var initialized = false;
    var cachedScenes = null;
    var lastRenderedValue = null;
    var enhanced = false;
    var modeSet = false;
    var frame = null;
    var mediaQuery = null;
    var desktopQuery = null;
    var items = [];
    var targetProgress = 0;
    var renderedProgress = 0;
    var lastFrameTime = 0;
    var snapNextFrame = true;
    var ownerDocument = root.ownerDocument;
    var view = ownerDocument.defaultView || window;

    function reducedMotion() {
      return mediaQuery && mediaQuery.matches;
    }

    function shouldEnhance() {
      return root.dataset.wmHasEmptyMedia !== 'true' && items.length > 1 && !reducedMotion() && desktopQuery && desktopQuery.matches;
    }

    function ease(value) {
      var bounded = clamp(value, 0, 1);
      return bounded * bounded * (3 - 2 * bounded);
    }

    function equalRows(count) {
      var rowCount = Math.max(count - 1, 1);
      var rows = [];
      for (var index = 0; index < rowCount; index += 1) rows.push(100 / rowCount);
      return rows;
    }

    function initialGrid(count) {
      return {
        column: 100 / count,
        rows: equalRows(count),
      };
    }

    function focusGrid(count, focusIndex) {
      var focusArea = 70;
      var auxiliaryCount = Math.max(count - 1, 1);
      var auxiliaryArea = (100 - focusArea) / auxiliaryCount;
      if (focusIndex === 0) {
        return {
          column: focusArea,
          rows: equalRows(count),
        };
      }

      var rightWidth = 100 - auxiliaryArea;
      var rows = [];
      for (var index = 1; index < count; index += 1) {
        rows.push((index === focusIndex ? focusArea : auxiliaryArea) / rightWidth * 100);
      }
      return {
        column: auxiliaryArea,
        rows: rows,
      };
    }

    function interpolateGrid(fromGrid, toGrid, amount) {
      var eased = ease(amount);
      return {
        column: fromGrid.column + (toGrid.column - fromGrid.column) * eased,
        rows: fromGrid.rows.map(function (fromRow, index) {
          return fromRow + (toGrid.rows[index] - fromRow) * eased;
        }),
      };
    }

    function fullMedia(count) {
      var media = [];
      for (var index = 0; index < count; index += 1) media.push(1);
      return media;
    }

    function focusMedia(count, focusIndex) {
      var media = [];
      for (var index = 0; index < count; index += 1) media.push(index === focusIndex ? 1 : 0);
      return media;
    }

    function initialState(count) {
      return {
        grid: initialGrid(count),
        media: fullMedia(count),
      };
    }

    function focusState(count, focusIndex) {
      return {
        grid: focusGrid(count, focusIndex),
        media: focusMedia(count, focusIndex),
      };
    }

    function interpolateState(fromState, toState, amount) {
      var eased = ease(amount);
      return {
        grid: interpolateGrid(fromState.grid, toState.grid, amount),
        media: fromState.media.map(function (fromPresence, index) {
          return fromPresence + (toState.media[index] - fromPresence) * eased;
        }),
      };
    }

    function timeline(count) {
      var balanced = initialState(count);
      var scenes = [];
      var previous = balanced;
      // Let each image change carry the scroll; short holds preserve a reading pause.
      scenes.push({ name: 'initial-hold', weight: 5, from: balanced, to: balanced, active: 'balanced' });
      items.forEach(function (_item, index) {
        var focused = focusState(count, index);
        scenes.push({ name: 'focus-' + String(index + 1) + '-transition', weight: 18, from: previous, to: focused, active: String(index) });
        scenes.push({ name: 'focus-' + String(index + 1) + '-hold', weight: 6, from: focused, to: focused, active: String(index) });
        previous = focused;
      });
      return scenes;
    }

    function resolveScene(progress) {
      var scenes = cachedScenes || (cachedScenes = timeline(items.length));
      var totalWeight = scenes.reduce(function (total, scene) { return total + scene.weight; }, 0);
      var position = clamp(progress, 0, 1) * totalWeight;
      var elapsed = 0;
      for (var index = 0; index < scenes.length; index += 1) {
        var scene = scenes[index];
        var sceneEnd = elapsed + scene.weight;
        if (position <= sceneEnd || index === scenes.length - 1) {
          var localProgress = scene.weight > 0 ? (position - elapsed) / scene.weight : 1;
          return {
            name: scene.name,
            active: scene.active,
            state: interpolateState(scene.from, scene.to, localProgress),
          };
        }
        elapsed = sceneEnd;
      }
      return {
        name: 'focus-' + String(items.length) + '-hold',
        active: String(Math.max(items.length - 1, 0)),
        state: focusState(items.length, Math.max(items.length - 1, 0)),
      };
    }

    function resetItems() {
      cachedScenes = null;
      lastRenderedValue = null;
      root.style.removeProperty('--wm-grid-left');
      root.style.removeProperty('--wm-grid-rows');
      items.forEach(function (item) {
        item.classList.remove('is-active');
        item.style.removeProperty('--wm-rect-x');
        item.style.removeProperty('--wm-rect-y');
        item.style.removeProperty('--wm-rect-w');
        item.style.removeProperty('--wm-rect-h');
        item.style.removeProperty('--wm-media-presence');
        item.style.removeProperty('--wm-image-copy-presence');
        item.style.removeProperty('--wm-panel-copy-presence');
        delete item.dataset.wmMediaVisible;
        item.style.removeProperty('grid-column');
        item.style.removeProperty('grid-row');
        var caption = item.querySelector('figcaption');
        if (caption) caption.style.removeProperty('opacity');
        var link = item.querySelector('figcaption a');
        if (link) link.removeAttribute('tabindex');
      });
    }

    function applyScene(scene) {
      if (root.dataset.wmMovementPhase !== scene.name) root.dataset.wmMovementPhase = scene.name;
      if (root.dataset.wmMovementActive !== scene.active) root.dataset.wmMovementActive = scene.active;
      root.style.setProperty('--wm-grid-left', scene.state.grid.column.toFixed(3) + '%');
      root.style.setProperty('--wm-grid-rows', scene.state.grid.rows.map(function (row) {
        return row.toFixed(3) + '%';
      }).join(' '));
      items.forEach(function (item, index) {
        var mediaPresence = scene.state.media[index];
        var imageCopyPresence = ease(clamp((mediaPresence - .48) / .32, 0, 1));
        var panelCopyPresence = ease(clamp((.52 - mediaPresence) / .32, 0, 1));
        var mediaVisible = mediaPresence >= .66;
        var isActive = scene.active === String(index);
        item.classList.toggle('is-active', isActive);
        item.dataset.wmMediaVisible = mediaVisible ? 'true' : 'false';
        item.style.setProperty('--wm-media-presence', mediaPresence.toFixed(4));
        item.style.setProperty('--wm-image-copy-presence', imageCopyPresence.toFixed(4));
        item.style.setProperty('--wm-panel-copy-presence', panelCopyPresence.toFixed(4));
        item.style.gridColumn = index === 0 ? '1' : '2';
        item.style.gridRow = index === 0 ? '1 / -1' : String(index);
        var link = item.querySelector('figcaption a');
        if (link) {
          if (mediaVisible) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        }
      });
    }

    function measureProgress() {
      var pageY = view.scrollY || ownerDocument.documentElement.scrollTop || 0;
      var rootTop = root.getBoundingClientRect().top + pageY;
      var viewportHeight = Math.max(view.innerHeight || 1, 1);
      var releaseStart = rootTop + root.offsetHeight - viewportHeight;
      var storyDistance = Math.max(releaseStart - rootTop, 1);
      targetProgress = clamp((pageY - rootTop) / storyDistance, 0, 1);
    }

    function render(timestamp) {
      frame = null;
      if (!enhanced) return;
      measureProgress();

      var now = typeof timestamp === 'number' ? timestamp : Date.now();
      if (snapNextFrame) {
        renderedProgress = targetProgress;
        snapNextFrame = false;
      } else {
        var elapsed = lastFrameTime ? Math.min(Math.max(now - lastFrameTime, 1), 64) : 16;
        var smoothing = 1 - Math.exp(-elapsed / 72);
        renderedProgress += (targetProgress - renderedProgress) * smoothing;
      }
      lastFrameTime = now;

      var scene = resolveScene(renderedProgress);
      var renderedValue = renderedProgress.toFixed(4);
      if (lastRenderedValue !== renderedValue) {
        root.style.setProperty('--wm-story-progress', renderedValue);
        applyScene(scene);
        lastRenderedValue = renderedValue;
      }

      if (Math.abs(targetProgress - renderedProgress) > .0005) schedule();
    }

    function schedule() {
      if (!enhanced || frame !== null) return;
      if (view.requestAnimationFrame) frame = view.requestAnimationFrame(render);
      else frame = view.setTimeout(render, 16);
    }

    function cancel() {
      if (frame === null) return;
      if (view.cancelAnimationFrame) view.cancelAnimationFrame(frame);
      else view.clearTimeout(frame);
      frame = null;
    }

    function onResize() {
      var nextEnhanced = shouldEnhance();
      if (nextEnhanced !== enhanced) setMode();
      else {
        snapNextFrame = true;
        schedule();
      }
    }

    function setMode() {
      items = Array.prototype.slice.call(root.querySelectorAll(itemSelector));
      var nextEnhanced = shouldEnhance();
      if (modeSet && enhanced === nextEnhanced) {
        if (enhanced) schedule();
        return;
      }
      cancel();
      ownerDocument.removeEventListener('scroll', schedule);
      resetItems();
      enhanced = nextEnhanced;
      modeSet = true;
      root.style.removeProperty('--wm-story-count');
      root.style.removeProperty('--wm-story-progress');
      delete root.dataset.wmMovementTimeline;
      delete root.dataset.wmMovementPhase;
      if (enhanced) {
        root.dataset.wmMovementMode = 'shared-grid';
        root.dataset.wmMovementTimeline = 'shared-grid-panels-v1';
        root.style.setProperty('--wm-story-count', String(items.length));
        targetProgress = 0;
        renderedProgress = 0;
        lastFrameTime = 0;
        snapNextFrame = true;
        ownerDocument.addEventListener('scroll', schedule, { passive: true });
        render(Date.now());
      } else {
        root.dataset.wmMovementMode = 'editorial-grid';
        root.dataset.wmMovementActive = 'static';
      }
    }

    return {
      init: function () {
        if (initialized) this.destroy();
        mediaQuery = view.matchMedia('(prefers-reduced-motion: reduce)');
        desktopQuery = view.matchMedia('(min-width: 1024px)');
        if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', setMode);
        else if (mediaQuery.addListener) mediaQuery.addListener(setMode);
        if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onResize);
        else if (desktopQuery.addListener) desktopQuery.addListener(onResize);
        view.addEventListener('resize', onResize);
        setMode();
        initialized = true;
      },
      refresh: function () {
        this.destroy();
        this.init();
      },
      destroy: function () {
        cancel();
        ownerDocument.removeEventListener('scroll', schedule);
        view.removeEventListener('resize', onResize);
        if (mediaQuery) {
          if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', setMode);
          else if (mediaQuery.removeListener) mediaQuery.removeListener(setMode);
        }
        if (desktopQuery) {
          if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onResize);
          else if (desktopQuery.removeListener) desktopQuery.removeListener(onResize);
        }
        resetItems();
        delete root.dataset.wmMovementMode;
        delete root.dataset.wmMovementActive;
        delete root.dataset.wmMovementTimeline;
        delete root.dataset.wmMovementPhase;
        root.style.removeProperty('--wm-story-count');
        root.style.removeProperty('--wm-story-progress');
        enhanced = false;
        modeSet = false;
        targetProgress = 0;
        renderedProgress = 0;
        lastFrameTime = 0;
        snapNextFrame = true;
        mediaQuery = null;
        desktopQuery = null;
        initialized = false;
      },
    };
  }

  function createMovement(root) {
    if (root.hasAttribute('data-wm-photo-story')) {
      return window.WagmadePhotographicStory
        ? window.WagmadePhotographicStory({ root: root })
        : createStaticController(root, 'wmMovementActive', 'wmMovementMode', 'photographic-static');
    }
    return createMovementTimeline(root, '[data-wm-movement-shot]');
  }

  function createReveal(root, selector) {
    var observer;

    function stop() {
      if (observer) observer.disconnect();
      observer = null;
    }

    return {
      init: function () {
        stop();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) entry.target.classList.add('is-visible');
          });
        }, { threshold: 0.2 });
        root.querySelectorAll(selector).forEach(function (item) {
          observer.observe(item);
        });
      },
      refresh: function () {
        this.destroy();
        this.init();
      },
      destroy: function () {
        stop();
      },
    };
  }

  // Native scrolling avoids a perpetual RAF and preserves direct wheel/touch input.

  function registerHomepage() {
    var theme = window.WagmadeTheme;
    if (!theme) return false;
    // TASK-P1-116: isolate each section controller so a single failure never
    // blocks the remaining Homepage registration or the remaining section enhancements.
    try {
      theme.register('homepage-hero', '[data-wm-home-hero]', createHero);
      theme.register('homepage-collection', '[data-wm-home-collection]', createCollection);
      theme.register('homepage-category-focus', '[data-wm-category-story]', createCategory);
      theme.register('homepage-movement', '[data-wm-home-movement]', createMovement);
      theme.register('homepage-brand-close', '[data-wm-home-brand-close]', function (root) {
        return createReveal(root, '.wm-brand-close__copy, .wm-brand-close__newsletter');
      });
      theme.register('homepage-decisions', '[data-wm-home-decisions]', function (root) {
        return createReveal(root, '.wm-decisions__item');
      });
      theme.register('homepage-trust', '[data-wm-home-trust]', function (root) {
        return createReveal(root, '.wm-trust__item');
      });
    } catch (error) {
      // One broken section must not take the rest of the page down.
    }
    theme.init(document);
    window.WagmadeHomepageLoaded = true;
    return true;
  }

  var startAttempts = 0;
  var startTimer = null;
  var maxStartAttempts = 120;

  function start() {
    startTimer = null;
    if (registerHomepage()) return;
    startAttempts += 1;
    if (startAttempts < maxStartAttempts) startTimer = window.setTimeout(start, 50);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('load', function () {
    if (!window.WagmadeHomepageLoaded && startTimer === null) start();
  }, { once: true });
})();
